import { Cell } from 'react-table';

import { CostWarning } from 'bomOld/elements';
import { CellText } from 'coreOld/elements';
import { AlignText } from 'coreOld/enums';
import { formatDate, getInt, parseString, roundNumberWithUom } from 'coreOld/utils';
import { ICompanyPart } from 'partsOld/types';

import CompanyPartNotes from './CompanyPartNotes';
import { CorporatePartCostStatus } from 'corporate/enums';

export const columns = [
  {
    accessor: 'part',
    Header: () => <CellText mainText="Part Number" subTexts={['Company Part Number']} />,
    Cell: ({ row }: Cell<ICompanyPart>) => <CompanyPartNotes part={row.original} />,
    flex: 220,
  },
  {
    accessor: 'partBaseCode',
    Header: () => <CellText mainText="Base code" subTexts={['Pricing group']} />,
    Cell: ({ row }: Cell<ICompanyPart>) => {
      return (
        <>
          <CellText
            mainText={row.original.partBaseCode}
            subTexts={[
              row.original.pricingGroup1,
              row.original.pricingGroup2,
              row.original.pricingGroup3,
              row.original.pricingGroup4,
              row.original.pricingGroup5,
            ]}
            textEllipsis={true}
          />
        </>
      );
    },
    flex: 110,
  },
  {
    accessor: 'manufacturerName',
    Header: () => <CellText mainText="Manufacturer" subTexts={['Vendor - Vendor ID']} />,
    Cell: ({ row }: Cell<ICompanyPart>) => {
      const companyManufacturerName = row.original.companyManufacturerName || '';
      const companyManufacturerId = row.original.companyManufacturerId
        ? `-${row.original.companyManufacturerId}`
        : '';

      return (
        <CellText
          mainText={row.original.manufacturerName}
          subTexts={[
            row.original.companyManufacturerName
              ? `[${companyManufacturerName}${companyManufacturerId}]`
              : '-',
          ]}
        />
      );
    },
    flex: 220,
  },
  {
    accessor: 'descriptionLong',
    Header: 'Description',
    Cell: ({ row }: Cell<ICompanyPart>) => (
      <CellText mainText={parseString(row.original.descriptionLong)} />
    ),
    flex: 250,
  },
  {
    accessor: 'inventoryQuantity',
    Header: () => <CellText mainText="Available" alignText={AlignText.RIGHT} />,
    Cell: ({ row }: Cell<ICompanyPart>) => (
      <CellText mainText={getInt(row.original.inventoryQuantity)} alignText={AlignText.RIGHT} />
    ),
    flex: 100,
  },
  {
    accessor: 'externalUom',
    Header: () => <CellText mainText="Ext. UOM" subTexts={['Base UOM']} />,
    Cell: ({ row }: Cell<ICompanyPart>) => (
      <CellText mainText={row.original.externalUom || '-'} subTexts={[row.original.uom || '-']} />
    ),
    flex: 100,
  },
  {
    accessor: 'standardCost',
    Header: () => (
      <CellText mainText="Standard cost" subTexts={['Purchase cost']} alignText={AlignText.RIGHT} />
    ),
    Cell: ({ row }: Cell<ICompanyPart>) => (
      <CellText
        alignText={AlignText.RIGHT}
        mainText={roundNumberWithUom(
          row.original.bomStdCost,
          row.original.bomPriceDecimals,
          row.original.purchaseCostUom
        )}
        subTexts={[
          roundNumberWithUom(
            row.original.purchaseCost,
            row.original.bomPriceDecimals,
            row.original.purchaseCostUom
          ),
          <CostWarning
            key={row.original.id}
            costStatus={row.original.costingStatus as CorporatePartCostStatus}
          />,
        ]}
      />
    ),
    flex: 100,
  },
  {
    accessor: 'listPrice',
    Header: () => (
      <CellText mainText="List price" subTexts={['Floor price']} alignText={AlignText.RIGHT} />
    ),
    Cell: ({ row }: Cell<ICompanyPart>) => (
      <CellText
        alignText={AlignText.RIGHT}
        mainText={roundNumberWithUom(
          row.original.originalListPrice,
          row.original.bomPriceDecimals,
          row.original.originalListPriceUom
        )}
        subTexts={[
          roundNumberWithUom(
            row.original.originalFloorPrice,
            row.original.bomPriceDecimals,
            row.original.originalFloorPriceUom
          ),
        ]}
      />
    ),
    flex: 100,
  },
  {
    Header: () => <CellText mainText="Cost updated" subTexts={['Updated by']} />,
    accessor: 'updatedAt',
    Cell: ({ row }: Cell<ICompanyPart>) => {
      return <CellText mainText={formatDate(row.original.updatedAt, true)} />;
    },
    flex: 110,
  },
];
